import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

// Define Product type
interface Variant {
  weight: number;
  unit: string;
}
interface Product {
  productId: number;
  productName: string;
  productPrice: number;
  productDescription?: string;
  productImage?: string;
  variants: Variant[];
  quantity?: number;
  variant?: string;
  cartQuantity?: number;
}

interface ProductContextType {
  products: Product[];
  loading: boolean;
  fetchProducts: (searchQuery?: string) => Promise<void>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = async (searchQuery?: string) => {
    try {
      setLoading(true);

      const url = searchQuery
        ? `https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/AddProduct/GetAllProducts?query=${encodeURIComponent(searchQuery)}`
        : `https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/AddProduct/GetAllProducts`;

      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch products");

      const data: Product[] = await res.json();

      // Initialize quantity & variant
      const initialized = data.map((p) => ({
        ...p,
        quantity: 1,
        variant: p.variants.length > 0 ? `${p.variants[0].weight}${p.variants[0].unit}` : "100g",
        cartQuantity: 0,
      }));

      setProducts(initialized);
    } catch (err) {
      console.error("Error fetching products:", err);
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(); // fetch once on app start
  }, []);

  return (
    <ProductContext.Provider value={{ products, loading, fetchProducts }}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => {
  const ctx = useContext(ProductContext);
  if (!ctx) throw new Error("useProducts must be used inside ProductProvider");
  return ctx;
};
export default ProductContext;

